// Часовые пояса без библиотек: смещение пояса в нужный момент даёт Intl (с летним временем).
import { hmToMin, pad2 } from './dates.ts';

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(tz: string): Intl.DateTimeFormat {
  let f = formatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    formatters.set(tz, f);
  }
  return f;
}

// Что показывают часы в поясе tz в этот момент.
export function wallClock(instant: Date, tz: string): { date: string; hm: string; min: number } {
  const p: Record<string, string> = {};
  for (const part of formatterFor(tz).formatToParts(instant)) p[part.type] = part.value;
  const hour = Number(p.hour), minute = Number(p.minute);
  return { date: `${p.year}-${p.month}-${p.day}`, hm: pad2(hour) + ':' + pad2(minute), min: hour * 60 + minute };
}

// Смещение пояса от UTC в минутах в этот момент (Ереван: +240, Берлин летом: +120).
export function tzOffsetMin(instant: Date, tz: string): number {
  const p: Record<string, string> = {};
  for (const part of formatterFor(tz).formatToParts(instant)) p[part.type] = part.value;
  const wall = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute),
    Number(p.second));
  return Math.round((wall - Math.floor(instant.getTime() / 1000) * 1000) / 60000);
}

// Момент времени для «дата + время по поясу tz».
export function zonedInstant(date: string, hm: string, tz: string): Date {
  const [y = 0, m = 1, d = 1] = date.split('-').map(Number);
  const min = hmToMin(hm) ?? 0;
  const guess = Date.UTC(y, m - 1, d, Math.floor(min / 60), min % 60);
  let t = guess - tzOffsetMin(new Date(guess), tz) * 60000;
  t = guess - tzOffsetMin(new Date(t), tz) * 60000;   // второй проход — если рядом переход на летнее время
  return new Date(t);
}

// Время по другому поясу: 13:00 по Москве → 14:00 по Еревану.
export function convertWallTime(date: string, hm: string, fromTz: string, toTz: string): { date: string; hm: string } {
  const w = wallClock(zonedInstant(date, hm, fromTz), toTz);
  return { date: w.date, hm: w.hm };
}

export const TZ_CITIES: ReadonlyArray<readonly [string, string]> = [
  ['Asia/Yerevan', 'Ереван'], ['Europe/Moscow', 'Москва'], ['Asia/Tbilisi', 'Тбилиси'], ['Europe/Kiev', 'Киев'],
  ['Europe/Minsk', 'Минск'], ['Europe/Berlin', 'Берлин'], ['Europe/Paris', 'Париж'], ['Europe/Madrid', 'Мадрид'],
  ['Europe/Rome', 'Рим'], ['Europe/Amsterdam', 'Амстердам'], ['Europe/Prague', 'Прага'], ['Europe/Warsaw', 'Варшава'],
  ['Europe/Riga', 'Рига'], ['Europe/Vilnius', 'Вильнюс'], ['Europe/Tallinn', 'Таллин'], ['Europe/Helsinki', 'Хельсинки'],
  ['Europe/Belgrade', 'Белград'], ['Europe/Istanbul', 'Стамбул'], ['Europe/London', 'Лондон'],
  ['Europe/Lisbon', 'Лиссабон'], ['Asia/Nicosia', 'Лимассол'], ['Asia/Jerusalem', 'Тель-Авив'], ['Asia/Dubai', 'Дубай'],
  ['Asia/Baku', 'Баку'], ['Asia/Almaty', 'Алматы'], ['Asia/Tashkent', 'Ташкент'], ['Asia/Bishkek', 'Бишкек'],
  ['Asia/Bangkok', 'Бангкок'], ['Asia/Makassar', 'Бали'], ['Asia/Kolkata', 'Гоа'], ['Asia/Yekaterinburg', 'Екатеринбург'],
  ['Asia/Novosibirsk', 'Новосибирск'], ['Asia/Vladivostok', 'Владивосток'], ['Europe/Kaliningrad', 'Калининград'],
  ['Europe/Samara', 'Самара'], ['America/New_York', 'Нью-Йорк'], ['America/Los_Angeles', 'Лос-Анджелес'],
  ['America/Chicago', 'Чикаго'], ['America/Toronto', 'Торонто'], ['America/Sao_Paulo', 'Сан-Паулу'],
  ['Australia/Sydney', 'Сидней'],
];

export function tzCity(tz: string | null | undefined): string {
  return TZ_CITIES.find(([id]) => id === tz)?.[1] ?? (tz || '');
}

// «по Москве», «по Еревану» — дательный падеж города для напоминаний.
const CITY_DATIVE_EXCEPTIONS: Record<string, string> = {
  'Гоа': 'Гоа', 'Бали': 'Бали', 'Тбилиси': 'Тбилиси', 'Хельсинки': 'Хельсинки', 'Чикаго': 'Чикаго',
  'Торонто': 'Торонто', 'Баку': 'Баку', 'Алматы': 'Алматы', 'Сан-Паулу': 'Сан-Паулу', 'Лос-Анджелес': 'Лос-Анджелесу',
  'Нью-Йорк': 'Нью-Йорку', 'Тель-Авив': 'Тель-Авиву',
};

export function cityDative(city: string | null | undefined): string {
  const c = String(city ?? '');
  if (!c) return '';
  const exc = CITY_DATIVE_EXCEPTIONS[c];
  if (exc !== undefined) return exc;
  if (/[^а-яё]$/i.test(c)) return c;
  if (/а$/.test(c)) return c.replace(/а$/, 'е');
  if (/я$/.test(c)) return c.replace(/я$/, 'е');
  if (/й$/.test(c)) return c.replace(/й$/, 'ю');
  if (/ь$/.test(c)) return c.replace(/ь$/, 'ю');
  if (/[иоуыэ]$/.test(c)) return c;
  return c + 'у';
}
